import React from 'react';
import FlexibleTable from '@/stories/Components/FlexibleTable/FlexibleTable';
import { useTranslations } from 'use-intl';
import { toTranslationKey } from '@/utils/translations';
import {
	CreditBureauAccount,
	AccountType,
	ContractType,
	PaymentFrequency,
	ResponsibilityType
} from '@/graphql/__generated__/onboarding';
import {
	getAccountTypeText,
	getContractTypeText,
	getPaymentFrequencyText,
	getResponsibilityTypeText,
	getPaymentStatusText
} from './mapUtils';
import { getObservationCodeText } from './observationCodeUtils';
import ExpandableTable from './ExpandableTable';

interface AccountsProps {
	accounts?: CreditBureauAccount[] | null;
}

const Accounts: React.FC<AccountsProps> = ({ accounts }) => {
	const t = useTranslations('CreditBureau');
	if (!accounts || accounts.length === 0) return null;

	// Create new objects with translated keys and mapped values
	const translatedData = accounts.map((account) => {
		return Object.entries(account)
			.filter(([key]) => key !== '__typename')
			.reduce(
				(acc, [key, value]) => {
					const translatedKey = t(toTranslationKey(key));

					// Map special fields
					if (key === 'accountType' && value) {
						acc[translatedKey] = getAccountTypeText(value as AccountType, t);
					} else if (key === 'contractType' && value) {
						acc[translatedKey] = getContractTypeText(value as ContractType, t);
					} else if (key === 'paymentFrequency' && value) {
						acc[translatedKey] = getPaymentFrequencyText(value as PaymentFrequency, t);
					} else if (key === 'responsibilityType' && value) {
						acc[translatedKey] = getResponsibilityTypeText(value as ResponsibilityType, t);
					} else if (key === 'paymentStatus' && value) {
						acc[translatedKey] = getPaymentStatusText(value as string, t);
					} else if (key === 'observationCode' && value) {
						acc[translatedKey] = getObservationCodeText(value as string, t);
					} else {
						acc[translatedKey] = value;
					}

					return acc;
				},
				{} as Record<string, any>
			);
	});

	return (
		<ExpandableTable title={t('Accounts')}>
			<div className="max-w-4xl">
				<FlexibleTable data={translatedData} />
			</div>
		</ExpandableTable>
	);
};

export default Accounts;
